import React from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Clock, 
  Calendar, 
  User, 
  Building2,
  Briefcase,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface JornadaDetalleData {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string | null;
  horas_trabajadas: number | null;
  tipo_trabajo: string;
  descripcion_trabajo: string | null;
  es_hora_extra?: boolean;
  tecnico?: { nombre: string } | null;
  cliente?: { nombre: string } | null;
  orden_trabajo?: { numero_ot: string; titulo: string } | null;
  obra?: { nombre: string } | null;
}

interface JornadaDetalleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jornada: JornadaDetalleData | null;
}

const tipoTrabajoLabels: Record<string, string> = {
  instalacion: 'Instalación',
  puesta_en_marcha: 'Puesta en marcha',
  auditoria: 'Auditoría',
  visita_obra: 'Visita obra',
  correctivos: 'Correctivos',
  mantenimiento: 'Mantenimiento',
};

export const JornadaDetalleDialog: React.FC<JornadaDetalleDialogProps> = ({
  open,
  onOpenChange,
  jornada,
}) => {
  if (!jornada) return null;

  // Determinar el origen de la jornada
  const getOrigen = () => {
    if (jornada.orden_trabajo) {
      return {
        tipo: 'OT',
        nombre: `${jornada.orden_trabajo.numero_ot} - ${jornada.orden_trabajo.titulo}`,
        icon: <FileText className="h-4 w-4" />
      };
    }
    if (jornada.obra) {
      return {
        tipo: 'Obra',
        nombre: jornada.obra.nombre,
        icon: <Briefcase className="h-4 w-4" />
      };
    }
    if (jornada.cliente) {
      return {
        tipo: 'Cliente',
        nombre: jornada.cliente.nombre,
        icon: <Building2 className="h-4 w-4" />
      };
    }
    return null;
  };

  const origen = getOrigen();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-500" />
            Detalle de Jornada
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Fecha y horario */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Fecha</p>
                <p className="text-sm font-medium">
                  {format(new Date(jornada.fecha), 'EEEE, dd/MM/yyyy', { locale: es })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Horario</p>
                <p className="text-sm font-medium">
                  {jornada.hora_inicio} - {jornada.hora_fin || 'En curso'}
                </p>
              </div>
            </div>
          </div>

          {/* Técnico */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Técnico</p>
              <p className="text-sm font-medium">{jornada.tecnico?.nombre || 'Sin asignar'}</p>
            </div>
          </div>

          <Separator />

          {/* Origen */}
          {origen && (
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                {origen.icon}
                <p className="text-xs">{origen.tipo}</p>
              </div>
              <p className="text-sm font-medium">{origen.nombre}</p>
            </div>
          )}

          {/* Tipo de trabajo */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Tipo de Trabajo</p>
            <Badge variant="secondary">
              {tipoTrabajoLabels[jornada.tipo_trabajo] || jornada.tipo_trabajo}
            </Badge>
          </div>

          {/* Horas trabajadas */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div>
              <p className="text-xs text-muted-foreground">Horas Trabajadas</p>
              <p className="text-2xl font-bold">
                {jornada.horas_trabajadas?.toFixed(1) || '0'}h
              </p>
            </div>
            {jornada.es_hora_extra && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Horas Extra
              </Badge>
            )}
          </div>

          {/* Descripción */}
          {jornada.descripcion_trabajo && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Descripción del Trabajo</p>
              <p className="text-sm bg-muted/50 p-3 rounded-lg">{jornada.descripcion_trabajo}</p>
            </div>
          )}

          {/* Acciones */}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
